import React from 'react'
import { useTranslation } from 'react-i18next'
import { SettingsNestedMenu } from 'src/editor/layout/sidebar/settings/settings-nested-menu'
import { FormElementMenuItem } from './form-elements-menu-item'
import {
  FormElementTypeEnum,
  FormInterface,
} from 'src/shared/types/entities/form-interface'
import { getFormElementsSettingsIcons } from '../../utils/get-form-elements-settings-icons'
import { FormElementFieldSettings } from './form-element-field-settings'
import { FormElementCheckboxSettings } from './form-element-checkbox-settings'
import { FormElementSubmitButtonSettings } from './form-element-submit-button-settings'
import { CheckSquareIcon } from 'src/editor/shared/icons/check-square-icon'
import { Share02Icon } from 'src/editor/shared/icons/share-02-icon'
import { getContactSlugName } from 'src/editor/shared/utils/get-contact-field-title'
import { PlusIcon } from 'src/editor/shared/icons/plus-icon'
import { FormAddNewElementSettings } from './form-add-new-element-settings'
import { FormElementsSettingsNestedMenuItem } from './form-elements-settings-nested-menu-item'
import { useUpdateEntity } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { useManagement } from 'client/store'
import { getSettingsEditing } from 'client/reducers/managementReducer'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { setSettingsEditing } from 'client/store/management/managementActions'
import { FormElementDndItem } from './form-element-dnd-item'

interface FormElementsSettingsProps {
  entity: FormInterface
}

export const FormElementsSettings = ({ entity }: FormElementsSettingsProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const updateEntity = useUpdateEntity<FormInterface>()

  const editingFormElementId = useManagement(getSettingsEditing).elementId

  const onClose = () => {
    dispatch(
      setSettingsEditing({
        id: entity.id,
        type: entity.type,
        elementId: undefined,
      }),
    )
  }

  const onDelete = (id: string) => (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation()
    updateEntity({
      ...entity,
      elements: entity.elements.filter(element => element.id !== id),
    })
  }

  const onMove = (dragIndex: number, hoverIndex: number) => {
    updateEntity({
      ...entity,
      elements: entity.elements.map((element, index) =>
        index === dragIndex
          ? entity.elements[hoverIndex]
          : index === hoverIndex
            ? entity.elements[dragIndex]
            : element,
      ),
    })
  }

  return (
    <div className="flex flex-col gap-2">
      {entity.elements.map((element, index) => (
        <FormElementDndItem
          key={element.id}
          id={element.id}
          index={index}
          onMove={onMove}
        >
          {element.elementType === FormElementTypeEnum.Field ? (
            <FormElementsSettingsNestedMenuItem
              menuTitle={getContactSlugName(element.slug, t)}
              icon={getFormElementsSettingsIcons(element.slug)}
              onDelete={onDelete(element.id)}
              isOpen={
                editingFormElementId === undefined
                  ? undefined
                  : editingFormElementId === element.id
              }
              onClose={onClose}
            >
              <FormElementFieldSettings formEntity={entity} field={element} />
            </FormElementsSettingsNestedMenuItem>
          ) : element.elementType === FormElementTypeEnum.Checkbox ? (
            <FormElementsSettingsNestedMenuItem
              menuTitle={t('entity_settings.checkbox.header')}
              icon={CheckSquareIcon}
              onDelete={onDelete(element.id)}
              isOpen={
                editingFormElementId === undefined
                  ? undefined
                  : editingFormElementId === element.id
              }
              onClose={onClose}
            >
              <FormElementCheckboxSettings
                formEntity={entity}
                checkbox={element}
              />
            </FormElementsSettingsNestedMenuItem>
          ) : (
            <SettingsNestedMenu
              className="m-0"
              menuTitle={t('entity_settings.submit_button.header')}
              menuButtonClassName="p-0 mb-2"
              menuButtonLabel={
                <FormElementMenuItem
                  title={t('entity_settings.submit_button.header')}
                  icon={Share02Icon}
                />
              }
              isOpen={
                editingFormElementId === undefined
                  ? undefined
                  : editingFormElementId === element.id
              }
              onClose={onClose}
            >
              <FormElementSubmitButtonSettings
                formEntity={entity}
                submitButton={element}
              />
            </SettingsNestedMenu>
          )}
        </FormElementDndItem>
      ))}
      <SettingsNestedMenu
        withHorizontalLine
        className="m-0"
        menuTitle={t('entity_settings.add_new_element.header')}
        menuButtonClassName="p-0 mb-2"
        menuButtonIcon={() => <PlusIcon className="h-4 w-4" />}
        menuButtonLabel={t('entity_settings.add_new_element.header')}
      >
        <FormAddNewElementSettings entity={entity} />
      </SettingsNestedMenu>
    </div>
  )
}
